import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useContext, useMemo } from 'react'
import { DataPlayer, SettingsChordsType, SettingsPlayer } from 'player'
import { Context } from 'scala'
import { Task, TaskOperations } from '../../../../types'
import { useSettingsActions } from './use-settings-actions'
import { useSectionsEnabled } from '../../../../hooks/player/use-sections-enabled'
import { useLyricsEnabled } from '../../../../hooks/player/use-lyrics-enabled'
import { chordsLevel } from '../common/utils'
import { useDesktopApp } from '../../../../../../../packages/web-app-shell/src/hooks/misc/use-desktop-app'

export interface UseSettingsProps {
  task: Task
  mainOperations: TaskOperations[]
  enableEditTask?: boolean
  enableExport?: boolean
  data?: DataPlayer
}

export const useSettings = ({
  data,
  task,
  mainOperations,
  enableEditTask,
  enableExport
}: UseSettingsProps): SettingsPlayer => {
  const { i18n } = useLingui()
  const { isOwner, isDemo } = task
  const { user } = useContext(Context)
  const { isDesktopApp } = useDesktopApp()
  const actions = useSettingsActions({
    task,
    data
  })
  const limited = !user?.subscription?.isPremium && !isDemo
  const limitedForPro = !user?.subscription?.isPro && !isDemo
  const showBassOnChords = Boolean(user?.featureFlags?.webPlayerAdvancedChords)
  const { beatschords } = task?.summary?.v1

  const limitedMetronome = useMemo(
    () => beatschords?.params?.plan === 'free',
    [beatschords]
  )

  const lyricsEnabled = useLyricsEnabled({
    task,
    force: true
  })

  const isDialogue = useMemo(() => {
    const separateOperation = mainOperations?.find((i) =>
      i.name.includes('SEPARATE_')
    )

    return separateOperation?.params?.type === 'dialogue-music-effects-hifi'
  }, [mainOperations])

  const sectionsEnabled = useSectionsEnabled({
    task,
    force: true
  })

  return useMemo(
    () => ({
      backButton: true,
      volumeMaster: true,
      loop: true,
      actions,
      changeOperations: true,
      shortcuts: true,
      lyrics: {
        view: lyricsEnabled,
        edit: isOwner || enableEditTask,
        isDialogue,
        limited
      },
      sections: {
        view: sectionsEnabled,
        edit: isOwner || enableEditTask,
        editLocked: limited,
        limited
      },
      pitch: {
        view: true,
        edit: true,
        max: 12,
        // preLock: true
        // limited: limited ? 1 : undefined
        textNotAvailable: isDesktopApp
          ? i18n._(t`changing_key_chrome_message`)
          : i18n._(t`changing_key_unavailable_message`)
      },
      playbackRate: {
        view: true,
        edit: true,
        // preLock: true
        // limited: limited ? 10 : undefined
        textNotAvailable: isDesktopApp
          ? i18n._(t`changing_speed_chrome_message`)
          : i18n._(t`changing_speed_unavailable_message`)
      },
      countIn: {
        view: true,
        max: 16
        // limited: limited ? 4 : undefined
      },
      chords: {
        view: true,
        edit: true,
        limited,
        bass: showBassOnChords,
        type: chordsLevel[
          user?.preferences?.chordSkillLevel
        ] as SettingsChordsType
      },
      export: {
        view: isOwner || !!enableExport,
        limited
      },
      channels: {
        guitars: { limited, followControl: limited ? 'other' : undefined },
        lead: { limited, followControl: limited ? 'other' : undefined },
        rhythm: { limited, followControl: limited ? 'other' : undefined },
        electric: { limited, followControl: limited ? 'other' : undefined },
        acoustic: { limited, followControl: limited ? 'other' : undefined },
        strings: { limited, followControl: limited ? 'other' : undefined },
        piano: { limited, followControl: limited ? 'other' : undefined },
        keys: { limited, followControl: limited ? 'other' : undefined },
        wind: { limited, followControl: limited ? 'other' : undefined },
        cymbals: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        hat: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        kick: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        snare: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        toms: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        other_kit: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        dialogue: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        music: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        effects: {
          limitedForPro,
          followControl: limitedForPro ? 'other' : undefined
        },
        backing_vocals: {
          limited,
          followControl: limited ? 'other' : undefined
        },
        metronome: { limited: limited || limitedMetronome, showLock: limited }
      },
      waveforms: {
        guitars: { combinedOn: limited ? 'other' : undefined },
        lead: { combinedOn: limited ? 'other' : undefined },
        rhythm: { combinedOn: limited ? 'other' : undefined },
        electric: { combinedOn: limited ? 'other' : undefined },
        acoustic: { combinedOn: limited ? 'other' : undefined },
        strings: { combinedOn: limited ? 'other' : undefined },
        piano: { combinedOn: limited ? 'other' : undefined },
        keys: { combinedOn: limited ? 'other' : undefined },
        wind: { combinedOn: limited ? 'other' : undefined },
        cymbals: { combinedOn: limitedForPro ? 'other' : undefined },
        hat: { combinedOn: limitedForPro ? 'other' : undefined },
        kick: { combinedOn: limitedForPro ? 'other' : undefined },
        snare: { combinedOn: limitedForPro ? 'other' : undefined },
        toms: { combinedOn: limitedForPro ? 'other' : undefined },
        other_kit: { combinedOn: limitedForPro ? 'other' : undefined },
        backing_vocals: {
          combinedOn: limited ? 'other' : undefined
        }
      }
    }),
    [
      i18n,
      actions,
      isOwner,
      limited,
      limitedForPro,
      lyricsEnabled,
      sectionsEnabled,
      isDialogue,
      showBassOnChords,
      enableEditTask,
      enableExport,
      isDesktopApp,
      user?.preferences?.chordSkillLevel,
      limitedMetronome
    ]
  )
}
